import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosHeaders,
} from 'axios';

import { API_CONFIG, ENV_CONFIG } from '../config';
import { getLoginAppUrl } from '../config/runtimeUrls';
import { authService } from './auth/AuthService';
import { createTraceSpan, endTraceSpan, getTraceHeaders } from '../utils/traceContext';
import { unwrapNormalizedPayload } from '../utils/normalizeApiResponse';

// Global refresh coalescing to avoid multiple concurrent refresh calls
let refreshInFlight: Promise<boolean> | null = null;

// Enterprise-grade error response interface
interface ErrorResponse {
  success: boolean;
  error: string;
  error_id: string;
  timestamp: string;
  details?: any;
}

// Enterprise-grade API client with unified configuration
class ApiClient {
  private client: AxiosInstance;
  private retryCount: number = 0;
  private isRefreshing: boolean = false;
  private failedQueue: Array<{
    resolve: (value: AxiosResponse | PromiseLike<AxiosResponse>) => void;
    reject: (reason?: any) => void;
    config: AxiosRequestConfig & { _retry?: boolean };
  }> = [];
  private inflight: Map<string, Promise<AxiosResponse>> = new Map();

  constructor() {
    // Configure base URL with runtime host routing (no hardcoded ports)
    const API_BASE_URL = API_CONFIG.BASE_URL || '/api';

    console.log('🔧 [ApiClient] Initializing with BASE_URL:', API_BASE_URL);
    console.log('🔧 [ApiClient] API_CONFIG.BASE_URL:', API_CONFIG.BASE_URL);
    console.log('🔧 [ApiClient] NODE_ENV:', process.env.NODE_ENV);
    if (String(process.env.REACT_APP_AUTH_TRACE || '').toLowerCase() === 'true') {
      try {
        console.info('🔎 [ApiClient] Runtime baseURL', {
          baseURL: API_BASE_URL,
          origin: typeof window !== 'undefined' ? window.location.origin : 'n/a',
          host: typeof window !== 'undefined' ? window.location.host : 'n/a',
        });
      } catch (_) {}
    }

    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      // Do NOT set Content-Type globally here; let interceptors decide per request
      headers: {
        ...API_CONFIG.SECURITY_HEADERS,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor for authentication, tenant ID, and logging
    this.client.interceptors.request.use(
      config => {
        // ENTERPRISE CACHE BUSTING: Force fresh data on every request
        if (config.method === 'get' || config.method === 'GET') {
          config.params = {
            ...config.params,
            _cacheBust: Date.now(),
          };
        }

        // Enhanced path normalization for enterprise-grade API endpoint handling
        if (config.url && !config.url.startsWith('http')) {
          // Ensure proper path format (leading slash for relative paths)
          if (!config.url.startsWith('/')) {
            config.url = `/${config.url}`;
          }

          // Special handling for baseURL ending with /api
          // ENTERPRISE FIX: prevent accidental double-prefix: /api + /api/... => /api/api/...
          const baseURL = (config.baseURL || '').toString().replace(/\/+$/, '');
          if (baseURL.endsWith('/api') && typeof config.url === 'string') {
            if (config.url === '/api') {
              config.url = '/';
            } else if (config.url.startsWith('/api/')) {
              // Strip the leading /api to keep exactly one /api in the final URL
              config.url = config.url.slice('/api'.length);
            }
          }

          // Remove any duplicate slashes in the path (except protocol://)
          config.url = config.url.replace(/([^:]\/)\/+/g, '$1');
        }

        // Debug logging for request URL
        const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
        console.log(`🌐 [ApiClient] ${config.method?.toUpperCase()} ${fullUrl}`);

        // ✅ PHASE 8: Trace propagation (auto-generate if missing)
        try {
          const existingHeaders = (config.headers || {}) as Record<string, string>;
          const existingTraceId =
            existingHeaders['x-trace-id'] ||
            existingHeaders['X-Trace-Id'] ||
            existingHeaders['x-trace-id'.toLowerCase()];
          const span = createTraceSpan({
            traceId: existingTraceId || undefined,
            apiName: config.url || null,
            pipelineStage: 'http',
            tip: config.method,
          });
          (config as any).__traceSpanId = span.spanId;
          config.headers = {
            ...existingHeaders,
            ...getTraceHeaders(span),
          } as any;
        } catch (_) {}

        // Add authentication token for all requests (including TTS)
        const token = this.getAuthToken();
        if (token) {
          const hasAuthHeader = Boolean((config.headers as any)?.Authorization);
          if (!hasAuthHeader) {
            (config.headers as any).Authorization = `Bearer ${token}`;
          }
        }

        // Add tenant ID to all requests with enhanced sources (header only)
        try {
          const tenantId = this.getTenantId();
          if (tenantId) {
            config.headers['X-Tenant-ID'] = tenantId;
            config.headers['X-Organization-ID'] = tenantId; // Alternative header for compatibility
          }
        } catch (e) {
          console.warn('Failed to add tenant ID to headers:', e);
        }

        // Strip accidental tenantId from params to avoid 401s and duplication
        if (config.params && (config.params as any).tenantId) {
          try {
            delete (config.params as any).tenantId;
          } catch (_) {}
        }

        // Add enterprise headers with proper typing
        const headers = new AxiosHeaders(config.headers);
        const isMultipart = typeof FormData !== 'undefined' && config.data instanceof FormData;
        Object.entries(API_CONFIG.SECURITY_HEADERS).forEach(([key, value]) => {
          // Skip Content-Type for multipart so the browser sets the boundary
          if (isMultipart && key.toLowerCase() === 'content-type') return;
          headers.set(key, value);
        });
        // If multipart, explicitly remove any lingering content-type to allow proper boundary
        if (isMultipart && headers.has('Content-Type')) {
          headers.delete('Content-Type');
        }
        // Ensure X-Agent-System is set explicitly for login and auth calls
        if (!headers.has('X-Agent-System')) {
          headers.set('X-Agent-System', 'omniscient');
        }
        config.headers = headers;

        // Log request in development
        if (ENV_CONFIG.IS_DEVELOPMENT) {
          console.log(`🔍 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
            headers: config.headers,
            data: config.data,
            params: config.params,
          });
        }

        // Coalesce identical GETs (in-flight de-dup)
        try {
          const method = (config.method || 'GET').toString().toLowerCase();
          if (method === 'get') {
            const key = `${method} ${config.url}?${JSON.stringify(config.params || {})}`;
            const existing = this.inflight.get(key);
            if (existing) return existing as any;
            const p = Promise.resolve(config as any) as any;
            (config as any).__dedupeKey = key;
            this.inflight.set(key, p);
          }
        } catch (_) {}

        return config;
      },
      error => {
        console.error('❌ API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling, token refresh, and retry logic
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        try {
          const k = (response.config as any)?.__dedupeKey;
          if (k) this.inflight.delete(k);
        } catch (_) {}
        try {
          const traceSpanId = (response.config as any)?.__traceSpanId;
          if (traceSpanId) {
            endTraceSpan(traceSpanId, {
              status: 'ok',
              apiName: response.config?.url || null,
            });
          }
        } catch (_) {}

        // Normalize enterprise responses: { success, data, error } -> data
        try {
          const normalized = unwrapNormalizedPayload(response.data);
          if (normalized !== response.data) {
            response.data = normalized;
          }
        } catch (_) {}

        // Log successful response in development
        if (ENV_CONFIG.IS_DEVELOPMENT) {
          console.log(`✅ API Response: ${response.status} ${response.config.url}`, {
            data: response.data,
          });
        }

        // Capture tenant context on login
        try {
          const url = response.config?.url || '';
          if (typeof url === 'string' && /\/auth\/login$/.test(url)) {
            const data: any = response.data;
            const tenantId = data?.tenantId || data?.user?.tenantId;
            if (tenantId) {
              localStorage.setItem('tenantId', tenantId);
            }
          }
        } catch (e) {
          // no-op
        }

        // Reset retry count on success
        this.retryCount = 0;
        return response;
      },
      async (error: any) => {
        // NOTE: Keep this permissive; Axios error typing differs across versions and
        // we don't want type-level issues to block runtime-safe cancellation behavior.
        const axiosError: any = error;
        try {
          const k = (axiosError.config as any)?.__dedupeKey;
          if (k) this.inflight.delete(k);
        } catch (_) {}

        // ✅ Avoid noisy logs for intentional aborts/cancels (common on unmount + route changes)
        // This also prevents Jest "Cannot log after tests are done" when components correctly abort requests.
        const isCanceled =
          axios.isCancel(axiosError) ||
          (axiosError as any)?.code === 'ERR_CANCELED' ||
          (axiosError as any)?.name === 'CanceledError';
        if (isCanceled) {
          try {
            const traceSpanId = (axiosError.config as any)?.__traceSpanId;
            if (traceSpanId) {
              endTraceSpan(traceSpanId, {
                status: 'error',
                error: 'REQUEST_CANCELED',
                apiName: axiosError.config?.url || null,
              });
            }
          } catch (_) {}
          return Promise.reject(axiosError);
        }

        try {
          const traceSpanId = (axiosError.config as any)?.__traceSpanId;
          if (traceSpanId) {
            endTraceSpan(traceSpanId, {
              status: 'error',
              error: axiosError?.message || 'REQUEST_FAILED',
              apiName: axiosError.config?.url || null,
            });
          }
        } catch (_) {}

        console.error('❌ API Response Error:', {
          status: axiosError.response?.status,
          statusText: axiosError.response?.statusText,
          url: axiosError.config?.url,
          method: axiosError.config?.method,
          data: axiosError.response?.data,
        });

        // Handle 404 errors consistently with enterprise-grade error information
        if (axiosError.response?.status === 404) {
          console.error('❌ [ApiClient] 404 Not Found:', {
            url: axiosError.config?.url,
            fullURL: `${axiosError.config?.baseURL || ''}${axiosError.config?.url || ''}`,
            method: axiosError.config?.method,
            headers: axiosError.config?.headers,
          });

          return Promise.reject({
            code: 'ENDPOINT_NOT_FOUND',
            message: `API endpoint not found: ${axiosError.config?.url || 'unknown'}`,
            path: axiosError.config?.url,
            status: 404,
            userMessage:
              'Requested service is temporarily unavailable. Our team has been notified.',
            timestamp: new Date().toISOString(),
            originalError: axiosError,
          });
        }

        // ENTERPRISE V2: Handle 401 with AuthService refresh
        if (axiosError.response?.status === 401) {
          const originalRequest = (axiosError.config || {}) as AxiosRequestConfig & {
            __retried?: boolean;
            __rateRetried?: boolean;
            __sessionExpiredNotified?: boolean;
          };
          const urlStr = String(originalRequest?.url || '');
          console.log('🔒 401 detected on:', urlStr);
          // Never refresh for /auth/* endpoints
          if (/\/auth\//.test(urlStr)) {
            console.log('🔒 401 on auth endpoint, rejecting immediately');
            return Promise.reject(axiosError);
          }

          // ✅ ENTERPRISE GUARD: If the user has no token context, do NOT attempt refresh.
          // Prevents refresh storms / logout loops when already logged out or missing credentials.
          try {
            const accessToken = this.getAuthToken();
            const refreshTokenRaw = (localStorage.getItem('refresh_token') ||
              localStorage.getItem('auth_v2_refreshToken') ||
              localStorage.getItem('refreshToken') ||
              '') as string;
            const refreshToken = String(refreshTokenRaw || '').trim();
            const lower = refreshToken.toLowerCase();
            const hasRefreshToken = Boolean(
              refreshToken && lower !== 'null' && lower !== 'undefined'
            );
            if (!accessToken && !hasRefreshToken) {
              if (this.shouldSuppressAuthRedirect()) {
                console.log('🔒 401 during auth bootstrap; suppressing redirect');
                return Promise.reject(axiosError);
              }
              console.log('🔒 401 but no auth tokens present; redirecting to login');
              this.emitAuthEvent('auth:session-expired', { reason: '401_no_tokens', url: urlStr });
              this.handleAuthError();
              return Promise.reject(axiosError);
            }
          } catch (_) {}

          if (!originalRequest.__retried) {
            if (!originalRequest.__sessionExpiredNotified && !this.shouldSuppressAuthRedirect()) {
              originalRequest.__sessionExpiredNotified = true;
              this.emitAuthEvent('auth:session-expired', { reason: '401', url: urlStr });
            }
            // ENTERPRISE V2: ONLY use AuthService refresh - no legacy fallback
            if (!refreshInFlight) {
              console.log('🔄 Starting token refresh...');
              refreshInFlight = authService.refresh().finally(() => {
                refreshInFlight = null;
              });
            } else {
              console.log('🔄 Waiting for in-flight refresh...');
            }
            const refreshSucceeded = await refreshInFlight;
            const newToken = refreshSucceeded ? this.getAuthToken() : null;
            if (newToken) {
              console.log('✅ Token refreshed, retrying request');
              this.emitAuthEvent('auth:session-recovered', { reason: 'refresh_success', url: urlStr });
              originalRequest.__retried = true;
              originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: `Bearer ${newToken}`,
              } as any;
              // Re-inject tenant defensively
              try {
                const tid = this.getTenantId();
                if (tid) (originalRequest.headers as any)['X-Tenant-ID'] = tid;
              } catch (_) {}
              return this.client.request(originalRequest);
            } else {
              // ✅ ENTERPRISE: hard-stop polling/retries on auth failure
              try {
                // Clear state + storage so no further requests send Bearer null/invalid
                await authService.logout();
              } catch (_) {}
              this.emitAuthEvent('auth:session-expired', { reason: '401_refresh_failed', url: urlStr });
              if (!this.shouldSuppressAuthRedirect()) {
                this.handleAuthError();
              } else {
                console.log('🔒 401 refresh failed during auth bootstrap; suppressing redirect');
              }
            }
          } else {
            console.log('🔒 Request already retried once');
          }
          // Bubble up single 401 if refresh not available or already retried
          return Promise.reject(axiosError);
        }

        // Specific handling for tenant assignment errors
        if (axiosError.response?.status === 403) {
          const data: any = axiosError.response.data;
          const originalRequest = (axiosError.config || {}) as AxiosRequestConfig & {
            __accessDeniedNotified?: boolean;
          };
          if (!originalRequest.__accessDeniedNotified) {
            originalRequest.__accessDeniedNotified = true;
            this.emitAuthEvent('auth:access-denied', {
              code: data?.code || 'ACCESS_DENIED',
              url: axiosError.config?.url,
            });
          }
          if (data?.code === 'TENANT_NOT_ASSIGNED') {
            console.error(
              '🚫 Tenant not assigned for user. Blocking access until admin assigns tenant.'
            );
          }
        }

        // Honor 429 Retry-After once; do not spin
        if (axiosError.response?.status === 429) {
          const originalRequest = (axiosError.config || {}) as AxiosRequestConfig & {
            __rateRetried?: boolean;
          };
          if (!originalRequest.__rateRetried) {
            const retryAfterHeader = (axiosError.response.headers as any)?.['retry-after'];
            const retryAfter = Number(
              retryAfterHeader || (axiosError.response.data as any)?.retryAfter || 0
            );
            if (retryAfter > 0 && retryAfter <= 60) {
              await new Promise(r => setTimeout(r, retryAfter * 1000));
              originalRequest.__rateRetried = true;
              return this.client.request(originalRequest as any);
            }
          }
          return Promise.reject(axiosError);
        }

        if (
          this.shouldRetry(axiosError as AxiosError) &&
          this.retryCount < API_CONFIG.RETRY.MAX_ATTEMPTS
        ) {
          return this.handleRetry(axiosError as AxiosError);
        }

        // Handle specific error types
        return this.handleError(axiosError as AxiosError);
      }
    );
  }

  private getAuthToken(): string | null {
    try {
      const token = authService.getAccessToken();

      if (token && ENV_CONFIG.IS_DEVELOPMENT) {
        console.log('🔐 [V2] Using accessToken from AuthService');
      }

      if (token) return token;
    } catch (error) {
      console.error('🔐 [V2] AuthService not available:', error);
    }

    // Fallback: localStorage tokens (canonical + legacy)
    let token =
      localStorage.getItem('auth_token') ||
      localStorage.getItem('auth_v2_accessToken') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      '';
    token = String(token || '').trim();
    const lower = token.toLowerCase();
    if (!token || lower === 'null' || lower === 'undefined' || token.split('.').length < 3) {
      return null;
    }
    if (ENV_CONFIG.IS_DEVELOPMENT) {
      console.log('🔐 [V2] Using accessToken from localStorage fallback');
    }
    return token;
  }

  private getTenantId(): string | null {
    // Simplified tenant ID retrieval for SaaS architecture
    try {
      // 1. Direct tenant ID storage (set during login)
      const tenantId = localStorage.getItem('tenantId');
      if (tenantId && /^[0-9a-fA-F]{24}$/.test(tenantId)) {
        return tenantId;
      }

      // 2. User object fallback
      const user = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        if (userData.tenantId && /^[0-9a-fA-F]{24}$/.test(userData.tenantId)) {
          // Store for future direct access
          localStorage.setItem('tenantId', userData.tenantId);
          return userData.tenantId;
        }
      }

      // 3. Development fallback
      if (process.env.REACT_APP_DEFAULT_TENANT_ID) {
        const envTenantId = process.env.REACT_APP_DEFAULT_TENANT_ID;
        if (/^[0-9a-fA-F]{24}$/.test(envTenantId)) {
          return envTenantId;
        }
      }
    } catch (error) {
      console.warn('Error resolving tenant ID in API client:', error);
    }

    return null;
  }

  private shouldRetry(error: AxiosError): boolean {
    // ✅ ENTERPRISE FIX: Never retry 401 errors (authentication failures should not retry)
    if (error.response?.status === 401) {
      if (ENV_CONFIG.IS_DEVELOPMENT) {
        console.log('🚫 [ApiClient] Skipping retry for 401 Unauthorized');
      }
      return false;
    }

    // ✅ ENTERPRISE FIX: Never retry file uploads
    const config = error.config as any;
    if (config && config['x-no-retry']) {
      if (ENV_CONFIG.IS_DEVELOPMENT) {
        console.log('🚫 Retry disabled for this request (file upload)');
      }
      return false;
    }

    // Enterprise-grade retry logic with comprehensive error categorization
    const retryableStatuses = [408, 429, 500, 502, 503, 504] as const;
    const retryableErrorCodes = ['ECONNABORTED', 'ENOTFOUND', 'ETIMEDOUT'] as const;

    // Type-safe status code checking with explicit boolean conversion
    const hasRetryableStatus: boolean = Boolean(
      error.response?.status &&
        retryableStatuses.includes(error.response.status as (typeof retryableStatuses)[number])
    );

    // Type-safe error code checking with explicit boolean conversion
    const hasRetryableErrorCode: boolean = Boolean(
      error.code &&
        typeof error.code === 'string' &&
        retryableErrorCodes.includes(error.code as (typeof retryableErrorCodes)[number])
    );

    // Enterprise-grade retry decision with comprehensive logging
    const shouldRetryRequest: boolean = hasRetryableStatus || hasRetryableErrorCode;

    if (ENV_CONFIG.IS_DEVELOPMENT && shouldRetryRequest) {
      console.log('🔄 Retry condition detected:', {
        status: error.response?.status,
        errorCode: error.code,
        hasRetryableStatus,
        hasRetryableErrorCode,
        url: error.config?.url,
        method: error.config?.method,
      });
    }

    return shouldRetryRequest;
  }

  private async handleRetry(error: AxiosError): Promise<AxiosResponse> {
    this.retryCount++;
    const delay =
      API_CONFIG.RETRY.DELAY * Math.pow(API_CONFIG.RETRY.BACKOFF_MULTIPLIER, this.retryCount - 1);

    console.log(
      `🔄 Retrying request (${this.retryCount}/${API_CONFIG.RETRY.MAX_ATTEMPTS}) in ${delay}ms`
    );

    await new Promise(resolve => setTimeout(resolve, delay));

    // Retry the original request
    if (error.config) {
      return this.client.request(error.config);
    }

    throw error;
  }

  private handleError(error: AxiosError): never {
    // Enterprise-grade error handling with proper typing
    const errorResponse: ErrorResponse = {
      success: false,
      error: 'Enterprise service unavailable',
      error_id: this.generateErrorId(),
      timestamp: new Date().toISOString(),
    };

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data: any = error.response.data;
      const backendMessage =
        data?.error?.message ||
        data?.message ||
        (typeof data?.error === 'string' ? data.error : null);

      switch (status) {
        case 401: {
          const isAuthRelated =
            data?.code === 'AUTHENTICATION_FAILED' || data?.code === 'AUTHENTICATION_REQUIRED';
          errorResponse.error =
            backendMessage || (isAuthRelated ? 'Authentication required' : 'Request unauthorized');
          break;
        }
        case 403:
          errorResponse.error = backendMessage || 'Access denied';
          break;
        case 404:
          errorResponse.error = backendMessage || 'Service endpoint not found';
          break;
        case 429:
          errorResponse.error = backendMessage || 'Rate limit exceeded';
          break;
        case 500:
          errorResponse.error = backendMessage || 'Internal server error';
          break;
        case 503:
          errorResponse.error = backendMessage || 'Service temporarily unavailable';
          break;
        default:
          errorResponse.error = backendMessage || (error.response.data as any)?.error || 'Request failed';
      }

      errorResponse.details = error.response.data;
      (errorResponse as any).status = status;
    } else if (error.request) {
      // Network error
      const isTimeout =
        error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout');
      errorResponse.error = isTimeout ? 'Request timed out. Please try again.' : 'Network connection failed';
    } else {
      // Other error
      errorResponse.error = error.message || 'Unknown error occurred';
    }

    const err = new Error(errorResponse.error);
    (err as any).data = errorResponse;
    (err as any).status = (errorResponse as any).status || error.response?.status || null;
    throw err;
  }

  private handleAuthError(): void {
    if (this.shouldSuppressAuthRedirect()) {
      return;
    }

    // Clear only auth token keys (no localStorage.clear)
    this.clearAuthTokens();

    // Redirect to login if not already there
    if (window.location.pathname !== '/login') {
      const returnToPath =
        window.location.pathname + window.location.search + window.location.hash;
      // Preserve the current route for post-login redirect
      try {
        sessionStorage.setItem('postLoginRedirect', returnToPath);
      } catch (_) {}
      const loginHost = getLoginAppUrl();
      const returnTo = `${window.location.origin}${returnToPath}`;
      const loginTargetBase = loginHost ? `${loginHost}/login` : '/login';
      const separator = loginTargetBase.includes('?') ? '&' : '?';
      window.location.href = `${loginTargetBase}${separator}from=${encodeURIComponent(returnTo)}`;
    }
  }

  private shouldSuppressAuthRedirect(): boolean {
    try {
      const initComplete = authService.isInitComplete?.() ?? true;
      const continueInProgress = authService.isAuthContinueInProgress?.() ?? false;
      const authState = authService.getState?.();
      if (!initComplete || continueInProgress) return true;
      if (authState === 'INITIALIZING' || authState === 'AUTHENTICATING') return true;
    } catch (_) {}
    try {
      if (typeof window !== 'undefined') {
        const path = window.location.pathname || '';
        if (path.startsWith('/auth/continue')) return true;
      }
    } catch (_) {}
    return false;
  }

  private clearAuthTokens(): void {
    const tokenKeys = [
      'auth_token',
      'refresh_token',
      // Legacy token keys (auth-related only)
      'auth_v2_accessToken',
      'auth_v2_refreshToken',
      'accessToken',
      'refreshToken',
      'authToken',
      'token',
    ];
    tokenKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (_) {}
    });
  }

  private emitAuthEvent(name: string, detail?: Record<string, any>): void {
    try {
      if (typeof window === 'undefined') return;
      window.dispatchEvent(new CustomEvent(name, { detail }));
    } catch (_) {}
  }

  private generateErrorId(): string {
    return `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Public methods for API calls
  public async get<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(endpoint, config);
    return response.data;
  }

  public async post<T = any>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    // Allow per-request override to bypass interceptors (e.g., OAuth callback)
    if (config?.headers && (config.headers as any)['X-Skip-Auth'] === 'true') {
      const client = axios.create({
        baseURL: this.client.defaults.baseURL,
        withCredentials: true,
        timeout: config.timeout || this.client.defaults.timeout,
        headers: config.headers,
      });
      const raw = await client.post(endpoint, data, config);
      return raw.data;
    }

    const response = await this.client.post<T>(endpoint, data, config);
    return response.data;
  }

  public async put<T = any>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(endpoint, data, config);
    return response.data;
  }

  public async delete<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(endpoint, config);
    return response.data;
  }

  public async patch<T = any>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.patch<T>(endpoint, data, config);
    return response.data;
  }

  // File upload method
  public async uploadFile<T = any>(endpoint: string, file: File, additionalData?: any): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
    }

    // ✅ ENTERPRISE FIX: Disable retries for file uploads + extended timeout
    // File uploads should NEVER be retried as they're not idempotent
    // Large files need extended timeout (up to 10 minutes)
    const response = await this.client.post<T>(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000, // 10 minutes for large files (was 120s)
      // Mark as non-retryable by adding custom flag
      ['x-no-retry' as any]: true,
    });

    return response.data;
  }

  // Enhanced health check method for enterprise monitoring
  public async healthCheck(): Promise<boolean> {
    try {
      const response = await this.client.get('/health', { timeout: 5000 });
      return response.status === 200;
    } catch (error) {
      console.error('API health check failed:', error);
      return false;
    }
  }

  // Check API connectivity and endpoint availability
  public async checkApiConnectivity(): Promise<{
    healthy: boolean;
    endpoints: Record<string, boolean>;
  }> {
    const endpoints = {
      health: false,
      auth: false,
      documents: false,
      email: false,
    };

    try {
      // Check health endpoint
      endpoints.health = await this.healthCheck();

      // Check critical endpoints with timeout
      const checkEndpoint = async (path: string): Promise<boolean> => {
        try {
          const response = await this.client.get(path, { timeout: 3000 });
          return response.status < 500;
        } catch (error: any) {
          // 404 is acceptable for endpoint check, 500+ is not
          return error.response?.status < 500;
        }
      };

      endpoints.auth = await checkEndpoint('/auth/status');
      endpoints.documents = await checkEndpoint('/documents/memos');
      endpoints.email = await checkEndpoint('/email/accounts');

      const healthy = Object.values(endpoints).some(status => status);
      return { healthy, endpoints };
    } catch (error) {
      console.error('API connectivity check failed:', error);
      return { healthy: false, endpoints };
    }
  }

  // Get client instance for advanced usage
  public getClient(): AxiosInstance {
    return this.client;
  }
}

// Create and export singleton instance
const apiClient = new ApiClient();
export default apiClient;

// Export types for external use
export type { AxiosRequestConfig, AxiosResponse, AxiosError };
